import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getEtsyHealth } from '@/lib/etsy/health'
import toast from 'react-hot-toast'

interface EtsyListing {
  id: string
  title: string
  price: number
  currency: string
  url: string
  state: string
  quantity: number
  tags: string[]
  materials: string[]
  categoryPath: string[]
}

/**
 * Import from Etsy page
 * Allows users to import Etsy listings as products in Laurels
 * Based on wireframe: import-channel.html
 */
interface ImportPreview {
  listingId: string
  title: string
  price: number
  sku: string
  willImport: boolean
  reason?: string
  existingProductId?: string
  imageUrl?: string
}

interface ImportProgress {
  listingId: string
  title: string
  status: 'pending' | 'importing' | 'importing_media' | 'success' | 'error' | 'skipped'
  progress: number // 0-100
  mediaProgress?: number // 0-100 for media import step
  heroImageProgress?: number // 0-100 for hero image upload
  podImageProgress?: number // 0-100 for POD image upload
  heroImageUploaded?: boolean // Whether hero image upload completed
  podImageUploaded?: boolean // Whether POD image upload completed
  error?: string
  productId?: string
  listingIdCreated?: string
  existingProduct?: boolean
  existingProductId?: string
}

interface ImportResult {
  listingId: string
  success?: boolean
  skipped?: boolean
  productId?: string
  listingIdCreated?: string
  sku?: string
  existingProduct?: boolean
  reason?: string
  error?: string
}

export default function ImportEtsy() {
  const { brandId } = useParams<{ brandId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set())
  const [isImporting, setIsImporting] = useState(false)
  const [importInitiated, setImportInitiated] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState<ImportPreview[]>([])
  const [importProgress, setImportProgress] = useState<Map<string, ImportProgress>>(new Map())
  const [importResults, setImportResults] = useState<ImportResult[]>([])
  const [showCompletion, setShowCompletion] = useState(false)
  const queryClient = useQueryClient()

  type EtsyListingsResult = {
    listings: EtsyListing[]
    shopId?: number
    shopName?: string
    error?: string
    message?: string
  }

  const fetchEtsyListings = useCallback(async (): Promise<EtsyListingsResult> => {
    const resp = await fetch(brandId ? `/api/etsy/listings?brandId=${encodeURIComponent(brandId)}` : '/api/etsy/listings')
    const data = await resp.json()
    console.log('Etsy listings API response:', { status: resp.status, data })

    if (!resp.ok || data.error) {
      const error = data.error || 'unknown_error'
      let message = data.message || 'Failed to fetch Etsy listings'

      // Provide more helpful error messages for common issues
      if (error === 'missing_credentials') {
        message = 'Etsy integration is not configured. Please set up ETSY_CLIENT_ID and ETSY_CLIENT_SECRET in your environment variables.'
      } else if (error === 'missing_refresh_token') {
        message = 'Etsy is not connected. Please click "Connect to Etsy & Fetch Listings" to authorize the app.'
      } else if (error === 'etsy_user_failed') {
        message = 'Failed to authenticate with Etsy. Your access token may be expired. Please reconnect to Etsy.'
      } else if (error === 'no_user_id') {
        message = 'Failed to get your Etsy user information. Please try reconnecting to Etsy.'
      }

      console.error('Etsy listings API error:', { error, message, fullResponse: data })
      throw new Error(message)
    }

    // Log if listings array is empty
    if (!data.listings || data.listings.length === 0) {
      console.warn('Etsy API returned empty listings array:', { shopId: data.shopId, shopName: data.shopName, fullResponse: data })
    }

    return data
  }, [brandId])

  // Check for OAuth callback status
  useEffect(() => {
    const status = searchParams.get('etsy_status')
    const error = searchParams.get('etsy_error')
    if (status === 'connected') {
      // Clear the status param, refresh health check, and automatically fetch listings
      setSearchParams({}, { replace: true })
      queryClient.invalidateQueries({ queryKey: ['etsy', 'health', brandId || 'legacy'] })
      // Catch errors from fetchQuery to prevent unhandled promise rejections
      queryClient
        .fetchQuery({ queryKey: ['etsyListings', brandId || 'legacy'], queryFn: fetchEtsyListings })
        .catch((err) => {
        // Error is handled by React Query's error state, just log for debugging
        console.error('Error fetching listings:', err)
      })
    } else if (error) {
      // Show error message (could be enhanced with toast/notification)
      console.error('Etsy connection error:', error)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, queryClient, brandId, fetchEtsyListings])

  // Check Etsy connection status using health check API
  const {
    data: etsyHealth,
    isLoading: isEtsyHealthLoading,
  } = useQuery({
    queryKey: ['etsy', 'health', brandId || 'legacy'],
    queryFn: () => getEtsyHealth(brandId),
    staleTime: 60_000,
  })

  // Check if Etsy is connected (has tokens) by trying to fetch listings
  // If we get a "missing_refresh_token" error, we're not connected
  // If we get any other error or success, we're connected
  const {
    data: listingsData,
    isLoading: listingsLoading,
    error: listingsError,
  } = useQuery<EtsyListingsResult>({
    queryKey: ['etsyListings', brandId || 'legacy'],
    queryFn: fetchEtsyListings,
    enabled: false, // Only fetch when explicitly triggered
    retry: false,
  })

  const listings = listingsData?.listings || []
  // Consider Etsy connected if:
  // 1. Health check says connected
  // 2. We have listings data (successfully fetched)
  // 3. We have an error but it's NOT "missing_refresh_token" (means we're connected but something else failed)
  // 4. We've been redirected from OAuth callback (etsy_status=connected in URL)
  const hasConnectedStatus = searchParams.get('etsy_status') === 'connected'
  const isConnected = 
    etsyHealth?.connected === true ||
    listingsData !== undefined || 
    (listingsError && !listingsError.message.includes('missing_refresh_token')) ||
    hasConnectedStatus

  const handleToggleProduct = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  const handleConnectAndFetch = () => {
    // Redirect to OAuth start endpoint
    const frontendOrigin =
      typeof window !== 'undefined' && typeof window.location?.origin === 'string' ? window.location.origin : ''
    const frontendOriginParam = frontendOrigin ? `&frontendOrigin=${encodeURIComponent(frontendOrigin)}` : ''
    window.location.href = `/api/etsy/oauth/start?redirect=${brandId ? `/brands/${brandId}/import/etsy` : '/import/etsy'}${
      brandId ? `&brandId=${brandId}` : ''
    }${frontendOriginParam}&forceLogin=1`
  }

  const handleFetchListings = async () => {
    // Trigger the listings query
    // Error is handled by React Query's error state
    queryClient
      .fetchQuery({ queryKey: ['etsyListings', brandId || 'legacy'], queryFn: fetchEtsyListings })
      .catch((err) => {
      // Error is handled by React Query's error state, just log for debugging
      console.error('Error fetching listings:', err)
      })
  }

  const handleSelectAll = () => {
    if (selectedProducts.size === listings.length && listings.length > 0) {
      // All selected - deselect all
      setSelectedProducts(new Set())
    } else {
      // Not all selected - select all
      setSelectedProducts(new Set(listings.map((p) => p.id)))
    }
  }

  const handlePreview = async () => {
    const selectedListingIds = Array.from(selectedProducts)
    if (selectedListingIds.length === 0) return

    try {
      const resp = await fetch(
        brandId ? `/api/etsy/import/preview?brandId=${encodeURIComponent(brandId)}` : '/api/etsy/import/preview',
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listingIds: selectedListingIds }),
        }
      )

      if (!resp.ok) {
        let errorData
        try {
          errorData = await resp.json()
        } catch {
          // If response is not JSON, use status text
          errorData = { error: 'http_error', message: `HTTP ${resp.status}: ${resp.statusText}` }
        }
        // Show error in preview UI
        const errorPreview: ImportPreview = {
          listingId: 'error',
          title: 'Preview Error',
          price: 0,
          sku: '',
          willImport: false,
          reason: errorData.message || errorData.error || `Failed to preview import (${resp.status})`,
        }
        setPreviewData([errorPreview])
        setShowPreview(true)
        return
      }

      const result = await resp.json()
      if (result.error) {
        // Show error in preview UI
        let errorMessage = result.message || result.error
        // Provide user-friendly messages for common error codes
        if (result.error === 'not_found') {
          errorMessage = result.message || 'API endpoint not found. Please ensure the worker is deployed and up to date.'
        } else if (result.error === 'not_connected') {
          errorMessage = result.message || 'Etsy account not connected. Please connect to Etsy first.'
        } else if (result.error === 'missing_credentials') {
          errorMessage = result.message || 'Credentials not configured. Please check your environment variables.'
        } else if (result.error === 'no_shops') {
          errorMessage = result.message || 'No shops found for this Etsy account.'
        }
        
        const errorPreview: ImportPreview = {
          listingId: 'error',
          title: 'Preview Error',
          price: 0,
          sku: '',
          willImport: false,
          reason: errorMessage,
        }
        setPreviewData([errorPreview])
        setShowPreview(true)
      } else {
        setPreviewData(result.previews || [])
        setShowPreview(true)
      }
    } catch (error) {
      console.error('Preview error:', error)
      // Show error in preview UI instead of alert
      const errorPreview: ImportPreview = {
        listingId: 'error',
        title: 'Preview Error',
        price: 0,
        sku: '',
        willImport: false,
        reason: error instanceof Error ? error.message : 'Unknown error occurred',
      }
      setPreviewData([errorPreview])
      setShowPreview(true)
    }
  }

  const handleImport = async () => {
    if (previewData.length === 0) {
      // If no preview, do preview first
      await handlePreview()
      return
    }

    // Disable button immediately when clicked
    setImportInitiated(true)
    setIsImporting(true)
    // Keep preview visible to show last preview results during import
    // setShowPreview(false)
    
    // Initialize progress tracking
    const progressMap = new Map<string, ImportProgress>()
    previewData.forEach((preview) => {
      progressMap.set(preview.listingId, {
        listingId: preview.listingId,
        title: preview.title,
        status: preview.willImport ? 'pending' : 'skipped',
        progress: 0,
        error: preview.reason,
        existingProductId: preview.existingProductId,
      })
    })
    setImportProgress(progressMap)
    setShowCompletion(false)
    setImportResults([])

    try {
      const selectedListingIds = Array.from(selectedProducts)
      
      // Call import API endpoint
      const resp = await fetch(
        brandId ? `/api/etsy/import?brandId=${encodeURIComponent(brandId)}` : '/api/etsy/import',
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listingIds: selectedListingIds,
        }),
        },
      )
      
      if (!resp.ok) {
        const error = await resp.json()
        throw new Error(error.message || 'Failed to import listings')
      }
      
      // Update progress as results come in
      const result = await resp.json()
      console.log('Import result:', result)
      
      // Update progress from results
      const updatedProgress = new Map(progressMap)
      
      // Check which listings have media import and track individual image uploads
      const listingsWithMedia = new Set<string>()
      result.results?.forEach((r: any) => {
        if (r.success && r.mediaImported) {
          listingsWithMedia.add(r.listingId)
          // Update progress with individual image upload status
          const existing = updatedProgress.get(r.listingId)
          if (existing) {
            existing.heroImageUploaded = r.heroImageUploaded || false
            existing.podImageUploaded = r.podImageUploaded || false
          }
        }
      })
      
      // Mark listings with media as importing media first (only if backend imported media)
      updatedProgress.forEach((progress) => {
        if (progress.status === 'pending' && listingsWithMedia.has(progress.listingId)) {
          progress.status = 'importing_media'
          progress.progress = 10
          progress.heroImageProgress = 0
          progress.podImageProgress = 0
        }
      })
      setImportProgress(new Map(updatedProgress))
      
      // Simulate individual image upload progress (hero first, then POD)
      const mediaProgressInterval = setInterval(() => {
        setImportProgress((prev) => {
          const updated = new Map(prev)
          updated.forEach((progress) => {
            if (progress.status === 'importing_media') {
              // Upload hero image first (0-40% overall, 0-100% hero progress)
              if (progress.heroImageProgress === undefined || progress.heroImageProgress < 100) {
                progress.heroImageProgress = Math.min((progress.heroImageProgress || 0) + 15, 100)
                progress.progress = 10 + Math.floor((progress.heroImageProgress / 100) * 30) // 10-40% overall
              } 
              // Then upload POD image (40-70% overall, 0-100% POD progress)
              else if (progress.podImageProgress === undefined || progress.podImageProgress < 100) {
                progress.podImageProgress = Math.min((progress.podImageProgress || 0) + 15, 100)
                progress.progress = 40 + Math.floor((progress.podImageProgress / 100) * 30) // 40-70% overall
              } 
              // Both images complete, move to product creation
              else {
                progress.status = 'importing'
                progress.progress = 70
                progress.heroImageProgress = 100
                progress.podImageProgress = 100
              }
            }
          })
          return updated
        })
      }, 200) // Update every 200ms
      
      // Store results for completion screen
      const allResults: ImportResult[] = []
      
        // Clear interval after a delay to allow media import to complete
        setTimeout(() => {
          clearInterval(mediaProgressInterval)
          // Mark all as importing (product creation phase), then success
          setImportProgress((prev) => {
            const updated = new Map(prev)
            updated.forEach((progress) => {
              if (progress.status === 'importing_media') {
                progress.status = 'importing'
                progress.progress = 70
                progress.heroImageProgress = 100
                progress.podImageProgress = 100
              }
            })
            return updated
          })
        
        // After a brief moment, mark as success
        setTimeout(() => {
          setImportProgress((prev) => {
            const updated = new Map(prev)
            updated.forEach((progress) => {
              if (progress.status === 'importing') {
                progress.status = 'success'
                progress.progress = 100
              }
            })
            return updated
          })
          
          // Then mark import as complete
          setIsImporting(false)
          setShowCompletion(true)
        }, 500) // Brief delay for "Creating product..." phase
      }, 2000) // 2 seconds for media import simulation
      
      // Then update with actual results
      // But don't overwrite 'importing_media' status immediately - let the simulation run first
      result.results?.forEach((r: any) => {
        const existing = updatedProgress.get(r.listingId)
        if (existing) {
          if (r.skipped) {
            existing.status = 'skipped'
            existing.progress = 100
            existing.error = r.reason
            allResults.push({
              listingId: r.listingId,
              skipped: true,
              reason: r.reason,
              productId: r.existingProductId,
            })
          } else if (r.success) {
            // Store result data but don't set status to 'success' yet if we're importing media
            // The media import simulation will transition to 'importing' then 'success'
            existing.productId = r.productId
            existing.listingIdCreated = r.listingIdCreated || r.listingId
            existing.existingProduct = r.existingProduct
            // Update individual image upload status from backend response
            existing.heroImageUploaded = r.heroImageUploaded || false
            existing.podImageUploaded = r.podImageUploaded || false
            // Only set to success if we're not in the media import phase
            if (existing.status !== 'importing_media') {
              existing.status = 'success'
              existing.progress = 100
            }
            allResults.push({
              listingId: r.listingId,
              success: true,
              productId: r.productId,
              listingIdCreated: r.listingIdCreated || r.listingId,
              sku: r.sku,
              existingProduct: r.existingProduct,
            })
          } else {
            existing.status = 'error'
            existing.progress = 100
            existing.error = r.error || 'Unknown error'
            allResults.push({
              listingId: r.listingId,
              error: r.error || 'Unknown error',
            })
          }
        }
      })
      
      result.errors?.forEach((e: any) => {
        const existing = updatedProgress.get(e.listingId)
        if (existing) {
          existing.status = 'error'
          existing.progress = 100
          existing.error = e.error
        }
        allResults.push({
          listingId: e.listingId,
          error: e.error,
        })
      })
      
      setImportProgress(new Map(updatedProgress))
      setImportResults(allResults)
      
      // Don't set isImporting to false here if we have media import in progress
      // The setTimeout above will handle completion after media import simulation
      const hasMediaImport = Array.from(updatedProgress.values()).some(p => p.status === 'importing_media')
      if (!hasMediaImport) {
        // No media import, mark as complete immediately
        setIsImporting(false)
        setShowCompletion(true)
      }
      // If hasMediaImport, the setTimeout above will handle completion
    } catch (error) {
      console.error('Import error:', error)
      setIsImporting(false)
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900">Import from Etsy</h1>
        </div>
      </header>

      <main className="p-4 space-y-6">
        {/* Step 1: Connect & Fetch */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
              1
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Connect & Fetch Listings</h2>
          </div>
          {isEtsyHealthLoading ? (
            <button
              type="button"
              disabled
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 opacity-60"
            >
              Checking connection...
            </button>
          ) : !isConnected ? (
            etsyHealth?.reason === 'shop_not_selected' ? (
              <button
                type="button"
                onClick={() => {
                  window.location.href = brandId ? `/brands/${brandId}/settings` : '/settings'
                }}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
              >
                Select Etsy Shop in Settings
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConnectAndFetch}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
              >
                Connect to Etsy & Fetch Listings
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={handleFetchListings}
              disabled={listingsLoading}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {listingsLoading ? 'Fetching Listings...' : listingsData ? 'Refresh Listings' : 'Fetch Listings'}
            </button>
          )}
          {listingsError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                Error fetching listings: {listingsError instanceof Error ? listingsError.message : 'Unknown error'}
              </p>
            </div>
          )}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How it works</h4>
            <p className="text-sm text-gray-700">
              We'll connect to your Etsy API and fetch all available listings. This may take a few moments.
            </p>
          </div>
        </section>

        {/* Step 2: Select Products */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
              2
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Select Listings to Import</h2>
          </div>

          {listingsLoading ? (
            <div className="text-center py-8 text-gray-600">Loading listings...</div>
          ) : listingsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-semibold mb-1">Error fetching listings</p>
              <p className="text-sm text-red-700">
                {listingsError instanceof Error ? listingsError.message : 'Unknown error occurred'}
              </p>
              <button
                type="button"
                onClick={handleFetchListings}
                className="mt-3 text-sm text-red-800 underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              {listingsData?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-semibold mb-1">Error fetching listings</p>
                  <p className="text-sm text-red-700">{listingsData.message || listingsData.error}</p>
                </div>
              ) : listingsData 
                ? `No listings found in shop "${listingsData.shopName || 'your shop'}".` 
                : 'Connect to Etsy to fetch your listings.'}
            </div>
          ) : (
            <>
              {listingsData?.shopName && (
                <div className="mb-3 text-sm text-gray-600">Shop: {listingsData.shopName}</div>
              )}
              <div className="space-y-2 mb-3">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleProduct(listing.id)}
                      className={`w-6 h-6 border-2 rounded flex-shrink-0 ${
                        selectedProducts.has(listing.id)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 bg-white'
                      }`}
                      aria-label={selectedProducts.has(listing.id) ? 'Deselect' : 'Select'}
                    >
                      {selectedProducts.has(listing.id) && (
                        <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{listing.title}</div>
                      <div className="text-xs text-gray-600">
                        {listing.price ? `£${listing.price.toFixed(2)}` : 'N/A'} • Qty: {listing.quantity || 0}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleSelectAll}
            className="w-full bg-gray-100 text-gray-900 border-2 border-gray-300 py-3 px-6 rounded-lg font-medium touch-target-min-44"
          >
            {selectedProducts.size === listings.length && listings.length > 0 ? 'Deselect All' : 'Select All'}
          </button>
        </section>

        {/* Step 3: Preview & Import */}
        {selectedProducts.size > 0 && !isImporting && !showPreview && (
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
                3
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Preview & Import</h2>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Ready to Import</h4>
              <p className="text-sm text-gray-700">
                {selectedProducts.size} {selectedProducts.size === 1 ? 'listing' : 'listings'} selected. Preview to see which will be imported and which already exist.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/"
                className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44"
              >
                Cancel
              </Link>
              <button
                type="button"
                onClick={handlePreview}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44"
              >
                Preview Import
              </button>
            </div>
          </section>
        )}

        {/* Preview Screen */}
        {showPreview && !isImporting && (
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
                3
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Import Preview</h2>
            </div>
            
            <div className="space-y-3 mb-4">
              {previewData.map((preview) => {
                // Treat preview errors (listingId === 'error') or any reason with error keywords as errors
                const isError = preview.listingId === 'error' || (preview.reason && (
                  preview.reason.toLowerCase().includes('failed') ||
                  preview.reason.toLowerCase().includes('error') ||
                  preview.reason.toLowerCase().includes('unknown') ||
                  preview.reason.toLowerCase().includes('not found') ||
                  preview.reason.toLowerCase().includes('not_connected') ||
                  preview.reason.toLowerCase().includes('missing_credentials')
                ))
                
                return (
                  <div
                    key={preview.listingId}
                    className={`border rounded-lg p-3 ${
                      preview.willImport
                        ? 'border-green-200 bg-green-50'
                        : isError
                        ? 'border-red-200 bg-red-50'
                        : 'border-yellow-200 bg-yellow-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Product Image */}
                      {preview.imageUrl && (
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          <img
                            src={preview.imageUrl}
                            alt={preview.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{preview.title}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {preview.listingId && preview.listingId !== 'error' && `Etsy ID: ${preview.listingId}`}
                          {preview.listingId && preview.listingId !== 'error' && preview.sku && ' • '}
                          {preview.sku && `SKU: ${preview.sku}`}
                          {(preview.listingId || preview.sku) && (preview.willImport || isError || preview.reason) && ' • '}
                          {preview.willImport ? (
                            <span className="ml-2 text-green-700 font-medium">✓ Will import</span>
                          ) : isError ? (
                            <span className="ml-2 text-red-700 font-medium">✗ {preview.reason}</span>
                          ) : (
                            <span className="ml-2 text-yellow-700 font-medium">⚠ {preview.reason || 'Will skip'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="flex gap-3">
              {!isImporting && !importInitiated && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPreview(false)
                      setPreviewData([])
                    }}
                    className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium touch-target-min-44"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={previewData.filter((p) => p.willImport).length === 0 || importInitiated}
                    className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44 disabled:opacity-60"
                  >
                    Import {previewData.filter((p) => p.willImport).length} {previewData.filter((p) => p.willImport).length === 1 ? 'Product' : 'Products'}
                  </button>
                </>
              )}
              {(isImporting || importInitiated) && (
                <div className="w-full text-center text-gray-600 py-3">
                  Import in progress... Preview results are shown above.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Completion Screen */}
        {showCompletion && !isImporting && (
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
                ✓
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Import Complete</h2>
            </div>

            <div className="space-y-4 mb-4">
              {importResults.filter((r) => r.success).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Successfully Imported ({importResults.filter((r) => r.success).length})
                  </h3>
                  <div className="space-y-2">
                    {importResults
                      .filter((r) => r.success)
                      .map((result) => {
                        const progress = importProgress.get(result.listingId!)
                        return (
                          <div key={result.listingId} className="border border-green-200 bg-green-50 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{progress?.title || `Listing ${result.listingId}`}</div>
                            <div className="text-xs text-gray-600 mt-1 space-y-1">
                              {result.productId && (
                                <div>
                                  Product: <Link to={`/products/${result.productId}`} className="text-blue-600 underline">View Product</Link>
                                  {result.existingProduct && <span className="text-gray-500 ml-1">(existing)</span>}
                                </div>
                              )}
                              {result.listingIdCreated && (
                                <div>
                                  Listing: <Link to={`/listings/${result.listingIdCreated}`} className="text-blue-600 underline">View Listing</Link>
                                </div>
                              )}
                              {result.sku && <div>SKU: {result.sku}</div>}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              {importResults.filter((r) => r.skipped).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Skipped ({importResults.filter((r) => r.skipped).length})
                  </h3>
                  <div className="space-y-2">
                    {importResults
                      .filter((r) => r.skipped)
                      .map((result) => {
                        const progress = importProgress.get(result.listingId!)
                        return (
                          <div key={result.listingId} className="border border-yellow-200 bg-yellow-50 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{progress?.title || `Listing ${result.listingId}`}</div>
                            <div className="text-xs text-yellow-700 mt-1">⚠ {result.reason || 'Skipped'}</div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              {importResults.filter((r) => r.error).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Errors ({importResults.filter((r) => r.error).length})
                  </h3>
                  <div className="space-y-2">
                    {importResults
                      .filter((r) => r.error)
                      .map((result) => {
                        const progress = importProgress.get(result.listingId!)
                        return (
                          <div key={result.listingId} className="border border-red-200 bg-red-50 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{progress?.title || `Listing ${result.listingId}`}</div>
                            <div className="text-xs text-red-700 mt-1">✗ {result.error}</div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Link
                to="/listings"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium text-center touch-target-min-44"
              >
                View All Listings
              </Link>
              <button
                type="button"
                onClick={() => {
                  setShowCompletion(false)
                  setImportResults([])
                  setImportProgress(new Map())
                  setShowPreview(false)
                  setPreviewData([])
                  setSelectedProducts(new Set())
                }}
                className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium touch-target-min-44"
              >
                Import More
              </button>
            </div>
          </section>
        )}

        {/* Import Progress Screen */}
        {isImporting && (
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-base">
                3
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Importing Products</h2>
            </div>

            <div className="space-y-3 mb-4">
              {Array.from(importProgress.values()).map((progress) => (
                <div
                  key={progress.listingId}
                  className="border rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{progress.title}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {progress.status === 'pending' && 'Waiting...'}
                        {progress.status === 'importing_media' && 'Importing media...'}
                        {progress.status === 'importing' && 'Creating product...'}
                        {progress.status === 'success' && '✓ Imported successfully'}
                        {progress.status === 'skipped' && `⚠ ${progress.error || 'Skipped'}`}
                        {progress.status === 'error' && `✗ Error: ${progress.error || 'Unknown error'}`}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{progress.progress}%</div>
                  </div>
                  {/* Overall progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        progress.status === 'success'
                          ? 'bg-green-600'
                          : progress.status === 'error'
                          ? 'bg-red-600'
                          : progress.status === 'skipped'
                          ? 'bg-yellow-600'
                          : 'bg-blue-600'
                      }`}
                      style={{ width: `${progress.progress}%` }}
                    />
                  </div>
                  {/* Individual image upload progress bars (shown when importing media) */}
                  {progress.status === 'importing_media' && (
                    <div className="mt-2 space-y-2">
                      {/* Hero image progress */}
                      {(progress.heroImageProgress !== undefined || progress.heroImageUploaded) && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-600">
                              Hero image {progress.heroImageUploaded ? '✓' : ''}
                            </span>
                            <span className="text-xs text-gray-500">
                              {progress.heroImageProgress !== undefined ? `${progress.heroImageProgress}%` : progress.heroImageUploaded ? '100%' : '0%'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full bg-purple-600 transition-all duration-300"
                              style={{ 
                                width: `${progress.heroImageProgress !== undefined ? progress.heroImageProgress : progress.heroImageUploaded ? 100 : 0}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {/* POD image progress */}
                      {(progress.podImageProgress !== undefined || progress.podImageUploaded) && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-600">
                              POD image {progress.podImageUploaded ? '✓' : ''}
                            </span>
                            <span className="text-xs text-gray-500">
                              {progress.podImageProgress !== undefined ? `${progress.podImageProgress}%` : progress.podImageUploaded ? '100%' : '0%'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full bg-indigo-600 transition-all duration-300"
                              style={{ 
                                width: `${progress.podImageProgress !== undefined ? progress.podImageProgress : progress.podImageUploaded ? 100 : 0}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
